import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  VStack,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  Button,
  Flex,
  Box,
  Text,
  HStack,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { Restaurant, useGetAllRestaurantsQuery } from 'src/utils/__generated__/graphql';
import SearchDropdown from './shared/SearchDropdown';
import { RestaurantListItem, PageHeader, ModernCard, LoadingSpinner } from './shared';
import { Specialities } from './shared/Specialities';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiUsers, FiTruck, FiDollarSign, FiShoppingBag } from 'react-icons/fi';
import { ElementType } from 'react';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: restaurantData, error, loading } = useGetAllRestaurantsQuery();

  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  if (loading) return <LoadingSpinner text="Loading restaurants..." />;
  if (error) {
    toast({
      title: 'Error',
      description: error.message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Data
          </Text>
          <Text color={textColor}>{error.message}</Text>
        </VStack>
      </ModernCard>
    );
  }

  const totalRestaurants = restaurantData?.restaurant.length || 0;
  const activeRestaurants = restaurantData?.restaurant.filter((r) => r.isActive).length || 0;

  return (
    <>
      <PageHeader
        title="Restaurant Management"
        subtitle="Manage and monitor all restaurants from one place"
        badge={
          <Badge colorScheme="brand" fontSize="sm" borderRadius="full" px={3} py={1}>
            {totalRestaurants} Restaurants
          </Badge>
        }
        actions={
          <Button
            leftIcon={<Icon as={FiPlus as ElementType} />}
            colorScheme="brand"
            size={{ base: 'md', md: 'lg' }}
            onClick={() => navigate('/admin/new-restaurant')}
            _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Add Restaurant</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Add</Text>
          </Button>
        }
      />

      {/* Stats Section */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }} mb={{ base: 6, md: 8 }}>
        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Total Restaurants
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={accentColor}>
              {totalRestaurants}
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiUsers as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>All registered restaurants</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>

        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Active Restaurants
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={accentColor}>
              {activeRestaurants}
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiTruck as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>Currently active</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>

        <ModernCard p={{ base: 4, md: 6 }} hover>
          <Stat>
            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
              Average Commission
            </StatLabel>
            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={accentColor}>
              {(
                (restaurantData?.restaurant ?? []).reduce((acc, r) => acc + (r?.admin_commission || 0), 0) /
                (totalRestaurants || 1)
              ).toFixed(1)}
              %
            </StatNumber>
            <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={2}>
                <Icon as={FiDollarSign as ElementType} boxSize={{ base: 3, md: 4 }} />
                <Text>Platform revenue</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </ModernCard>
      </SimpleGrid>

      {/* Main Content */}
      <ModernCard overflow="hidden">
        <Tabs variant="soft-rounded" colorScheme="brand" p={{ base: 4, md: 6 }}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            mb={{ base: 4, md: 6 }}
            gap={4}
          >
            <TabList>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiUsers as ElementType} boxSize={{ base: 4, md: 5 }} />
                  <Text fontSize={{ base: 'sm', md: 'md' }}>Restaurants</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <Icon as={FiShoppingBag as ElementType} boxSize={{ base: 4, md: 5 }} />
                  <Text fontSize={{ base: 'sm', md: 'md' }}>Specialities</Text>
                </HStack>
              </Tab>
            </TabList>

            <SearchDropdown />
          </Flex>

          <TabPanels>
            <TabPanel px={0}>
              {restaurantData?.restaurant.length === 0 ? (
                <VStack spacing={4} py={8} textAlign="center">
                  <Icon as={FiShoppingBag as ElementType} boxSize={12} color={textColor} opacity={0.5} />
                  <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                    No Restaurants Found
                  </Text>
                  <Text color={textColor} opacity={0.8}>
                    Start by adding your first restaurant to the platform.
                  </Text>
                </VStack>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
                    <Thead bg={useColorModeValue('gray.50', 'gray.800')}>
                      <Tr>
                        <Th display={{ base: 'none', md: 'table-cell' }}>Logo</Th>
                        <Th>Name</Th>
                        <Th display={{ base: 'none', md: 'table-cell' }}>Phone</Th>
                        <Th display={{ base: 'none', md: 'table-cell' }}>Email</Th>
                        <Th display={{ base: 'none', lg: 'table-cell' }}>Commission</Th>
                        <Th display={{ base: 'none', lg: 'table-cell' }}>Delivery</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {restaurantData?.restaurant.map((restaurant, index: number) => (
                        <RestaurantListItem key={index} restaurant={restaurant as Restaurant as any} />
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </TabPanel>
            <TabPanel px={0}>
              <Specialities />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ModernCard>
    </>
  );
};
